import { useMemo, useState } from "react";
import { useInfiniteQuery, keepPreviousData, type QueryKey } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { FeedFilters, FeedResult } from "@/api/types";

export const FEED_PAGE_SIZE = 50;

/**
 * Drop empty values and sort keys AND array values, so the query key is stable.
 *
 * React Query hashes object keys in a stable order but does NOT sort array elements.
 * Multi-select filters append on toggle, so deselecting then reselecting yields
 * ["b","a"] where ["a","b"] was — a different key, hence a spurious refetch and a
 * duplicate cache entry for an identical filter.
 */
function normalize(filters: FeedFilters): FeedFilters {
  const out: FeedFilters = {};
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value === undefined || value === "" || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length) out[key] = [...value].sort();
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Repeated-key form (?client_in=a&client_in=b) — axios' default serializer emits
 * `client_in[]=a`, which DRF's getlist() does not read.
 */
function buildParams(filters: FeedFilters, page: number, pageSize: number) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.append(key, String(value));
  }
  return params;
}

interface UseInfiniteFeedOptions {
  /**
   * Must start with the same root the feature's mutations invalidate (e.g.
   * ["orders", "feed"], never ["orders-feed"]) — invalidateQueries matches by prefix,
   * so a joined key silently stops the list refreshing after approve/reject/delete.
   */
  key: QueryKey;
  url: string;
  filters: FeedFilters;
  pageSize?: number;
  enabled?: boolean;
  staleTime?: number;
}

/**
 * Shared driver for the `/feed/` endpoints behind the "Załaduj więcej" button.
 *
 * Changing a filter resets to the first batch on its own: `filters` is part of the
 * query key, so it is simply a different query with its own initialPageParam.
 */
export function useInfiniteFeed<T extends { id: string }>({
  key,
  url,
  filters,
  pageSize = FEED_PAGE_SIZE,
  enabled = true,
  staleTime,
}: UseInfiniteFeedOptions) {
  const normalized = normalize(filters);

  const query = useInfiniteQuery({
    queryKey: [...key, normalized, pageSize],
    queryFn: ({ pageParam }) =>
      apiClient
        .get<FeedResult<T>>(url, { params: buildParams(normalized, pageParam as number, pageSize) })
        .then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.has_next ? last.meta.page + 1 : undefined),
    // Keep the current rows visible (dimmed) while a new filter loads, instead of
    // unmounting the list to a full-page spinner.
    placeholderData: keepPreviousData,
    enabled,
    ...(staleTime !== undefined && { staleTime }),
  });

  const [isFetchingAll, setIsFetchingAll] = useState(false);

  /** Fetch every remaining page in sequence — the "Załaduj wszystkie" button. */
  async function fetchAll() {
    if (isFetchingAll) return;
    setIsFetchingAll(true);
    try {
      let pages = query.data?.pages.length ?? 0;
      for (;;) {
        const res = await query.fetchNextPage();
        const got = res.data?.pages ?? [];
        // No new page means the fetch errored, was cancelled, or the query key
        // changed under us (filter switch) — either way, stop rather than spin.
        if (got.length <= pages) break;
        pages = got.length;
        if (!got[got.length - 1].meta.has_next) break;
      }
    } finally {
      setIsFetchingAll(false);
    }
  }

  const items = useMemo(() => {
    // Page-number pagination over a live, re-sortable table can surface one row on two
    // pages; dedupe so React never sees a duplicate key.
    const seen = new Set<string>();
    const rows: T[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const row of page.data) {
        if (!seen.has(row.id)) {
          seen.add(row.id);
          rows.push(row);
        }
      }
    }
    return rows;
  }, [query.data]);

  return { ...query, items, total: query.data?.pages[0]?.meta.count ?? 0, fetchAll, isFetchingAll };
}
